import { Slot } from "@radix-ui/react-slot";
import * as React from "react";
import { Icon, type IconName } from "~/components/icon";
import {
  cn,
  composeEventHandlers,
  isElementWithChildren,
  isReactElement,
} from "~/lib/utils";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  iconPrefix?: IconName;
  iconSuffix?: IconName;
  iconOnly?: boolean;
  variant?: "primary" | "secondary" | "tertiary" | "ghost" | "outline" | "link";
  size?: "small" | "medium" | "large";
  danger?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  rounded?: boolean;
  onClick?: (e: React.MouseEvent) => void;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (props: ButtonProps, ref: React.Ref<HTMLButtonElement>) => {
    const {
      asChild = false,
      children,
      iconPrefix,
      iconSuffix,
      iconOnly = false,
      variant = "primary",
      size = "medium",
      danger = false,
      loading = false,
      disabled = false,
      fullWidth = false,
      rounded = false,
      onClick = () => {},
      className,
      ...other
    } = props ?? {};

    const useAsChild = asChild && isReactElement(children);
    const Component = useAsChild ? Slot : "button";

    const isIcon = React.useMemo(() => {
      return (
        (iconPrefix && !iconSuffix && !children && size) ??
        (iconSuffix && !iconPrefix && !children && size) ??
        iconOnly === true ??
        false
      );
    }, [iconPrefix, iconSuffix, children, size, iconOnly]);

    const innerContent = useAsChild ? (
      React.cloneElement(children as React.ReactElement, {
        children: (
          <span className="fui-button__content">
            {iconPrefix && (
              <>
                <Icon name={iconPrefix} className="fui-button__icon" />
                <span className="sr-only">{iconPrefix}</span>
              </>
            )}
            {!iconOnly && isElementWithChildren(children) && (
              <span className="fui-button__label">
                {(children as React.ReactElement).props.children}
              </span>
            )}
            {iconSuffix && (
              <>
                <Icon name={iconSuffix} className="fui-button__icon" />
                <span className="sr-only">{iconSuffix}</span>
              </>
            )}
          </span>
        ),
      })
    ) : (
      <span className="fui-button__content">
        {iconPrefix && (
          <>
            <Icon name={iconPrefix} className="fui-button__icon" />
            <span className="sr-only">{iconPrefix}</span>
          </>
        )}
        {!iconOnly && children && (
          <span className="fui-button__label">{children}</span>
        )}
        {iconSuffix && (
          <>
            <Icon name={iconSuffix} className="fui-button__icon" />
            <span className="sr-only">{iconSuffix}</span>
          </>
        )}
      </span>
    );

    const handleClick = (evt: React.MouseEvent) => {};

    return (
      <Component
        ref={ref}
        className={cn(
          "fui-button",
          {
            "fui-button--primary": variant === "primary",
            "fui-button--secondary": variant === "secondary",
            "fui-button--tertiary": variant === "tertiary",
            "fui-button--ghost": variant === "ghost",
            "fui-button--outline": variant === "outline",
            "fui-button--link": variant === "link",
            "fui-button--loading": loading,
            "fui-button--disabled": disabled,
            "fui-button--full-width": fullWidth,
            "fui-button--rounded": rounded,
            "fui-button--icon-only": iconOnly || isIcon,
            "fui-button--small": size === "small",
            "fui-button--medium": size === "medium",
            "fui-button--large": size === "large",
            "fui-button--danger": danger,
          },
          className,
        )}
        disabled={disabled}
        onClick={composeEventHandlers([onClick, handleClick])}
        {...other}
      >
        {innerContent}
      </Component>
    );
  },
);

Button.displayName = "Button";
